import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { AvatarUpload } from "@/components/AvatarUpload";
import { Flame, Heart, TrendingUp, BarChart3, Camera } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMetricDefinitions } from "@/lib/useMetricDefinitions";
import { getLocalizedMetricName } from "@/lib/utils";
import { useDateFnsLocale } from "@/hooks/useDateFnsLocale";
import { toast } from "sonner";
import { FEATURED_SNAP_ID } from "@/lib/constants";

interface ReactionFeedProps {
  userId: string;
  onCollapseChange?: (isCollapsed: boolean) => void;
}

interface FeedItem {
  id: string;
  actor_id: string;
  item_type: string;
  metric_key?: string | null;
  reaction_type?: string | null;
  streak_count?: number | null;
  streak_metrics?: string[] | null;
  graph_count?: number | null;
  graph_names?: string[] | null;
  metadata?: {
    metric_name?: string;
    metric_label?: string;
    metric_emoji?: string;
    snap_date?: string;
    snap_id?: string;
  } | null;
  created_at: string;
  profiles: {
    username: string | null;

    avatar_url: string | null;
  };
  is_featured?: boolean;
}

const ITEMS_PER_LOAD = 10;
const MAX_ITEMS = 100;

export const ReactionFeed = ({
 userId, onCollapseChange }: ReactionFeedProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dateFnsLocale = useDateFnsLocale();
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_LOAD);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const hasMarkedAsChecked = useRef(false);
  
  // Query profile to get last_feed_check timestamp
  // NOTE: Uses unique query key to avoid cache collision with useProfile hook
  const { data: profileData } = useQuery({
    queryKey: ["profile-feed-check", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("last_feed_check")
        .eq("id", userId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
  
  // Fetch custom metrics for the current user
  const { data: customMetrics } = useQuery({
    queryKey: ["custom-metrics", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custom_metrics")
        .select("metric_key, metric_name, metric_emoji")
        .eq("user_id", userId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });

  // Fetch the featured snap to display at the top of the feed
  const { data: featuredSnap } = useQuery({
    queryKey: ["featured-snap", FEATURED_SNAP_ID],
    queryFn: async () => {
      const { data: snap, error: snapError } = await supabase
        .from("progress_snaps")
        .select("*")
        .eq("id", FEATURED_SNAP_ID)
        .single();

      if (snapError || !snap) {
        console.log("Featured snap not found or not accessible");
        return null;
      }

      // Fetch the snap owner's profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("username, avatar_url")
        .eq("id", snap.user_id)
        .single();

      return {
        snap,
        profile: profile || { username: null, avatar_url: null }
      };
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
  
  // Load viewed snaps from localStorage on mount
  const [viewedSnapFeedItems, setViewedSnapFeedItems] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(`viewedSnaps_${userId}`);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Fetch unseen feed count to determine initial collapsed state
  const { data: unseenCount } = useQuery({
    queryKey: ["unseen-feed-count", userId],
    queryFn: async () => {
      if (!userId) return 0;
      
      const { data: profile } = await supabase
        .from("profiles")
        .select("last_feed_check")
        .eq("id", userId)
        .single();
      
      if (!profile?.last_feed_check) return 0;
      
      const { count } = await supabase
        .from("feed_items")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .gt("created_at", profile.last_feed_check);
      
      return count || 0;
    },
    enabled: !!userId,
  });

  // Save viewed snaps to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(`viewedSnaps_${userId}`, JSON.stringify([...viewedSnapFeedItems]));
    } catch (error) {
      console.error("Failed to save viewed snaps:", error);
    }
  }, [viewedSnapFeedItems, userId]);
  
  // Handle clicking on a progress snap feed item
  const handleSnapClick = async (item: FeedItem) => {
    // For featured snaps or snaps with snap_id in metadata, navigate directly
    if (item.metadata?.snap_id) {
      // Mark this feed item as viewed BEFORE navigating
      const updatedViewedSnaps = new Set(viewedSnapFeedItems).add(item.id);
      setViewedSnapFeedItems(updatedViewedSnaps);
      
      // Save to localStorage synchronously before navigation
      try {
        localStorage.setItem(`viewedSnaps_${userId}`, JSON.stringify([...updatedViewedSnaps]));
      } catch (error) {
        console.error("Failed to save viewed snap:", error);
      }
      
      // Navigate to the snap view page
      navigate(`/snap/${item.metadata.snap_id}`);
      return;
    }
    
    if (!item.metadata?.metric_name || !item.metadata?.snap_date) return;
    
    try {
      // For snap_like items, the snap belongs to the feed owner (userId), not the actor (liker)
      // For progress_snap items, the snap belongs to the actor
      const snapOwnerId = item.item_type === 'snap_like' ? userId : item.actor_id;
      
      // Fetch the actual snap data
      const { data: snap, error } = await (supabase as any)
        .from("progress_snaps")
        .select("*")
        .eq("user_id", snapOwnerId)
        .eq("metric_name", item.metadata.metric_name)
        .eq("snap_date", item.metadata.snap_date)
        .maybeSingle();
        
      if (error) {
        // Handle permission errors gracefully
        if (error.code === '42501') {
          toast.error(t('notifications.toast.this_snap_is_no_longer_accessible'));
        } else {
          throw error;
        }
        return;
      }
      
      if (snap) {
        // Mark this feed item as viewed BEFORE navigating
        const updatedViewedSnaps = new Set(viewedSnapFeedItems).add(item.id);
        setViewedSnapFeedItems(updatedViewedSnaps);
        
        // Save to localStorage synchronously before navigation
        try {
          localStorage.setItem(`viewedSnaps_${userId}`, JSON.stringify([...updatedViewedSnaps]));
        } catch (error) {
          console.error("Failed to save viewed snap:", error);
        }
        
        // Navigate to the snap view page
        navigate(`/snap/${snap.id}`);
      } else {
        toast.error(t('notifications.toast.this_snap_is_no_longer_available'));
      }
    } catch (error) {
      console.error("Failed to fetch progress snap:", error);
      toast.error(t('notifications.toast.failed_to_load_snap'));
    }
  };

  // Fetch feed items for the current user
  const { data: feedItems, isLoading, error: queryError } = useQuery({
    queryKey: ["feed", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feed_items")
        .select(`
          id,
          actor_id,
          item_type,
          metric_key,
          reaction_type,
          streak_count,
          streak_metrics,
          metadata,
          created_at,
          profiles:actor_id(
            username,

            avatar_url
          )
        `)
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(MAX_ITEMS);

      if (error) {
        console.error("Feed query error:", error);
        throw error;
      }
      console.log("Feed items fetched:", data?.length || 0, "items");
      
      // Map the data to handle both direct columns and metadata
      let mappedItems = (data || []).map((item: any) => ({
        ...item,
        graph_count: item.graph_count || item.metadata?.graph_count,
        graph_names: item.graph_names || item.metadata?.graph_names,
      })) as FeedItem[];
      
      // Filter out snap-related items (progress_snap and snap_like) where the snap is no longer available
      // Validate in parallel for better performance
      const validationPromises = mappedItems.map(async (item) => {
        if ((item.item_type === 'snap_like' || item.item_type === 'progress_snap') && 
            item.metadata?.metric_name && item.metadata?.snap_date) {
          try {
            // Check if the snap still exists and is accessible
            const { data: snap, error: snapError } = await (supabase as any)
              .from("progress_snaps")
              .select("id")
              .eq("user_id", item.actor_id)
              .eq("metric_name", item.metadata.metric_name)
              .eq("snap_date", item.metadata.snap_date)
              .maybeSingle();
            
            // Filter out if:
            // 1. Snap doesn't exist (snap is null)
            // 2. Permission denied (error code 42501) - user deleted or made private
            // 3. Any other error
            if (!snap || snapError) {
              console.log(`Filtering out ${item.item_type} for unavailable snap:`, item.id, snapError?.code);
              return null;
            }
            
            return item;
          } catch (error) {
            // If validation fails for any reason, filter out the item
            console.log(`Filtering out ${item.item_type} due to validation error:`, item.id);
            return null;
          }
        } else {
          // Include all non-snap items
          return item;
        }
      });
      
      const validationResults = await Promise.all(validationPromises);
      const validatedItems = validationResults.filter((item): item is FeedItem => item !== null);
      
      return validatedItems;
    },
    enabled: !!userId,
  });

  // Fetch metric definitions to get display names and emojis
  // IMPORTANT: This hook must be called before any conditional returns to avoid React Hook error #310
  // USE THE SHARED HOOK - DO NOT create a custom query that corrupts the cache!
  const { data: metricDefinitions } = useMetricDefinitions();

  // Handle scroll for infinite loading
  // IMPORTANT: Must be defined before conditional returns to avoid React Hook error #310
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !feedItems || feedItems.length === 0) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    
    // Load more when scrolled near bottom (within 100px)
    if (scrollHeight - scrollTop - clientHeight < 100) {
      setDisplayCount(prev => Math.min(prev + ITEMS_PER_LOAD, feedItems.length));
    }
  }, [feedItems]);

  // Attach scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Mark feed as checked after items are displayed
  useEffect(() => {
    const markFeedAsChecked = async () => {
      // Don't mark as checked if:
      // 1. No user/items
      // 2. Already marked as checked in this session
      if (!userId || !feedItems || feedItems.length === 0 || hasMarkedAsChecked.current) return;
      
      try {
        // Wait a bit to ensure user sees the items before marking as checked
        await new Promise(resolve => setTimeout(resolve, 500));
        
        await supabase.rpc("update_last_feed_check", {
          user_uuid: userId
        });
        
        // Mark that we've checked in this session
        hasMarkedAsChecked.current = true;
        
        // Invalidate queries to update the badge
        queryClient.invalidateQueries({ queryKey: ["unseen-feed-count", userId] });
        queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      } catch (error) {
        console.error("Failed to update last feed check:", error);
      }
    };

    markFeedAsChecked();
  }, [userId, queryClient, feedItems]);

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-2">
          <span>📢</span>
          <h3 className="text-lg font-semibold">{t('ui.text.my_feed')}</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{t('ui.text.your_recent_social_feed')}</p>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Card>
    );
  }

  if (queryError) {
    console.error("Feed error:", queryError);
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-2">
          <span>📢</span>
          <h3 className="text-lg font-semibold">{t('ui.text.my_feed')}</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{t('ui.text.your_recent_social_feed')}</p>
        <div className="text-center py-3 text-red-500">
          <p className="text-sm">{t('ui.text.error_loading_feed')}</p>
          <p className="text-xs mt-1">{queryError.message}</p>
        </div>
      </Card>
    );
  }

  if (!feedItems || feedItems.length === 0) {
    // Even if no feed items, show the featured snap if available
    if (!featuredSnap) {
      return null;
    }
  }

  // Sort feed items: unopened snaps first, then everything else by date
  const sortedFeedItems = [...(feedItems || [])].sort((a, b) => {
    const aIsUnopenedSnap = a.item_type === "progress_snap" && !viewedSnapFeedItems.has(a.id);
    const bIsUnopenedSnap = b.item_type === "progress_snap" && !viewedSnapFeedItems.has(b.id);
    
    // Unopened snaps go to the front
    if (aIsUnopenedSnap && !bIsUnopenedSnap) return -1;
    if (!aIsUnopenedSnap && bIsUnopenedSnap) return 1;
    
    // Otherwise sort by date (newest first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // Create a synthetic feed item for the featured snap and prepend it
  const feedItemsWithFeatured = (() => {
    if (!featuredSnap) return sortedFeedItems;
    
    // Check if the featured snap is already in the feed (to avoid duplicates)
    const alreadyInFeed = sortedFeedItems.some(
      item => item.item_type === 'progress_snap' && 
              item.metadata?.snap_id === FEATURED_SNAP_ID
    );
    
    if (alreadyInFeed) return sortedFeedItems;
    
    // Create a synthetic feed item for the featured snap
    const featuredFeedItem: FeedItem = {
      id: `featured-${FEATURED_SNAP_ID}`,
      actor_id: featuredSnap.snap.user_id,
      item_type: 'progress_snap',
      metadata: {
        metric_name: featuredSnap.snap.metric_name,
        metric_label: featuredSnap.snap.metric_name,
        snap_date: featuredSnap.snap.snap_date,
        snap_id: featuredSnap.snap.id,
      },
      created_at: featuredSnap.snap.created_at,
      profiles: featuredSnap.profile,
      is_featured: true,
    };
    
    // Prepend the featured snap to the feed
    return [featuredFeedItem, ...sortedFeedItems];
  })();

  const displayedItems = feedItemsWithFeatured.slice(0, displayCount);
  const hasMore = displayCount < feedItemsWithFeatured.length;
  
  // Get last check time to determine which items are "read"
  const lastCheck = profileData?.last_feed_check;
  const lastCheckDate = lastCheck ? new Date(lastCheck) : null;
  
  // Helper to check if an item has been seen
  const isItemSeen = (item: FeedItem) => {
    // For snaps, use the localStorage system
    if (item.item_type === "progress_snap") {
      return viewedSnapFeedItems.has(item.id);
    }
    // For all other items, check against last_feed_check timestamp
    if (lastCheckDate) {
      return new Date(item.created_at) <= lastCheckDate;
    }
    return false;
  };

  const formatMetricName = (metricKey: string) => {
    return metricKey
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  const getMetricDisplay = (metricKey: string, metricLabel?: string, metricEmoji?: string) => {
    // If metadata already has the label and emoji, use those (from the snap creator)
    if (metricLabel && metricEmoji) {
      return `${metricEmoji} ${metricLabel}`;
    }
    
    // Check built-in metrics first - use translated label
    const metric = metricDefinitions?.[metricKey];
    if (metric) {
      const translatedLabel = getLocalizedMetricName(metricKey, t, metric.label);
      return `${metric.emoji} ${translatedLabel}`;
    }
    
    // Check custom metrics (fallback to logged-in user's metrics)
    if (metricKey.startsWith('custom_') && customMetrics) {
      const customMetric = customMetrics.find(cm => cm.metric_key === metricKey);
      if (customMetric) {
        return `${customMetric.metric_emoji || '📊'} ${customMetric.metric_name}`;
      }
    }
    
    return formatMetricName(metricKey);
  };

  return (
    <Card className="p-6 relative">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span>📢</span>
          <h3 className="text-lg font-semibold">{t('ui.text.my_feed')}</h3>
        </div>
        <span className="text-xs text-muted-foreground">
          {sortedFeedItems.length} {sortedFeedItems.length === MAX_ITEMS ? "+" : ""} {t('ui.text.total')}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">{t('ui.text.your_recent_social_feed')}</p>

      <div className="relative">
        <div 
          ref={scrollContainerRef}
          className="space-y-3 overflow-y-auto pr-2 overscroll-contain scroll-smooth"
          style={{ 
            maxHeight: 'calc(3 * 84px)',
            scrollbarWidth: 'thin',
            WebkitOverflowScrolling: 'touch'
          }}
        >
        {displayedItems.map((item) => {
          const username = item.profiles?.username;
          const displayName = username || "Unknown User";
          const timeAgo = formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: dateFnsLocale });

          return (
            <div
              key={item.id}
              onClick={() => {
                if (item.item_type === "progress_snap") {
                  handleSnapClick(item);
                } else if (item.item_type === "snap_like") {
                  handleSnapClick(item);
                } else {
                  username && navigate(`/${username}`);
                }
              }}
              className={`flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer ${
                isItemSeen(item) ? "opacity-50" : ""
              }`}
            >
              <AvatarUpload
                userId={item.actor_id}
                currentAvatarUrl={item.profiles?.avatar_url}
                username={username}
                size="sm"
                showUploadButton={false}
                clickable={false}
              />
              
              <div className="flex-1 min-w-0">
                {item.item_type === "reaction" ? (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">
                        {displayName}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {timeAgo}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground truncate">
                        {t('social.feed.reacted')}
                      </span>
                      {item.reaction_type === "fire" ? (
                        <Flame className="w-4 h-4 text-orange-500 fill-orange-500 flex-shrink-0" />
                      ) : (
                        <Heart className="w-4 h-4 text-pink-500 fill-pink-500 flex-shrink-0" />
                      )}
                      {item.metric_key && (
                        <span className="text-xs text-muted-foreground truncate">
                          to {getMetricDisplay(item.metric_key)}
                        </span>
                      )}
                    </div>
                  </>
                ) : item.item_type === "follow" ? (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">
                        {displayName}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {timeAgo}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {t('social.feed.started_following')}
                    </div>
                  </>
                ) : item.item_type === "follow_request" ? (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">
                        {displayName}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {timeAgo}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      sent you a follow request
                    </div>
                  </>
                ) : item.item_type === "graph_created" ? (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">
                        {displayName}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {timeAgo}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="text-xs text-muted-foreground truncate">
                        {item.graph_count && item.graph_count > 1 
                          ? `created ${item.graph_count} new graphs`
                          : `created a new graph`}
                        {item.graph_names && item.graph_names.length > 0 && (
                          <span className="ml-1">
                            • {item.graph_names.slice(0, 2).join(", ")}
                            {item.graph_names.length > 2 && ` +${item.graph_names.length - 2} more`}
                          </span>
                        )}
                      </span>
                    </div>
                  </>
                ) : item.item_type === "progress_snap" ? (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">
                        {displayName}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {timeAgo}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <div 
                        className="w-8 h-8 rounded flex items-center justify-center border-2 transition-colors flex-shrink-0 pointer-events-none"
                        style={{
                          borderColor: viewedSnapFeedItems.has(item.id) ? 'rgb(239 68 68)' : 'transparent',
                          backgroundColor: viewedSnapFeedItems.has(item.id) ? 'transparent' : 'rgb(239 68 68)'
                        }}
                      >
                        <Camera className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-xs text-muted-foreground truncate">
                          snapped progress
                          {item.metadata?.metric_name && ` • ${getMetricDisplay(item.metadata.metric_name, item.metadata.metric_label, item.metadata.metric_emoji)}`}
                        </span>
                        <span className="text-xs text-muted-foreground/60 italic truncate">
                          {viewedSnapFeedItems.has(item.id) ? 'Opened' : 'Tap to open'}
                        </span>
                      </div>
                    </div>
                  </>
                ) : item.item_type === "snap_like" ? (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">
                        {displayName}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {timeAgo}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground truncate">
                        liked your snap
                      </span>
                      <Heart className="w-4 h-4 text-pink-500 fill-pink-500 flex-shrink-0" />
                      {item.metadata?.metric_name && (
                        <span className="text-xs text-muted-foreground truncate">
                          for {getMetricDisplay(item.metadata.metric_name, item.metadata.metric_label, item.metadata.metric_emoji)}
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">
                        {displayName}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {timeAgo}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-xs text-muted-foreground truncate">
                        {item.streak_count === 1 ? "1 metric" : `${item.streak_count} metrics`} streak • 5+ days logged
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
        
        {hasMore && (
          <div className="flex items-center justify-center py-3 text-xs text-muted-foreground">{t('ui.text.scroll_for_more')}</div>
        )}
        </div>
        
        {/* Gradient fade at bottom to indicate scrollability */}
        {hasMore && (
          <div 
            className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom, transparent, hsl(var(--card)))'
            }}
          />
        )}
      </div>
    </Card>
  );
};

